import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  configureBrowserAuthTransport,
  consumePortalRedirectDiagnostic,
  recordPortalRedirectDiagnostic,
} from '../services/browserAuthTransport';
import { User } from '../types';
import { logger } from '../utils/logger';

interface BrowserAuthResponse {
  authenticated: boolean;
  user: User;
  csrfToken: string;
  expiresAt: number;
}

interface BrowserAuthConfigResponse {
  portalLoginUrl: string;
}

interface AuthInitialization {
  session: BrowserAuthResponse | null;
  config: BrowserAuthConfigResponse | null;
}

interface AuthContextValue {
  authenticated: boolean;
  loading: boolean;
  user: User | null;
  csrfToken: string | null;
  expiresAt: number | null;
  portalLoginUrl: string | null;
  error: string | null;
  logout: () => Promise<void>;
  retry: () => Promise<void>;
  markUnauthenticated: (message?: string) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const moduleLogger = logger.getModule('AuthContext');
const LEGACY_AUTH_STORAGE_KEYS = ['sg-chatbot-auth', 'sg_copilot_auth'];
const AUTH_REQUEST_TIMEOUT_MS = 20_000;
const AUTH_CONNECTION_ERROR_MESSAGE = 'Unable to reach the Coach server. Please try again.';

export const buildPortalLogoutUrl = (portalLoginUrl: string): string => {
  return new URL(portalLoginUrl, window.location.origin).toString();
};

const requestWithTimeout = async (path: string, request: RequestInit): Promise<Response> => {
  const abortController = new AbortController();
  const timeoutId = window.setTimeout(() => abortController.abort(), AUTH_REQUEST_TIMEOUT_MS);

  try {
    return await fetch(path, {
      ...request,
      signal: abortController.signal,
    });
  } catch (error) {
    if (abortController.signal.aborted) {
      throw new Error(AUTH_CONNECTION_ERROR_MESSAGE);
    }

    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const requestAuthState = async (
  path: '/api/auth/session' | '/api/auth/bootstrap',
  method: 'GET' | 'POST'
): Promise<Response> => requestWithTimeout(path, {
  method,
  credentials: 'same-origin',
  headers: { Accept: 'application/json' },
});

const readErrorMessage = async (response: Response): Promise<string> => {
  try {
    const body = await response.json();
    return typeof body?.detail === 'string'
      ? body.detail
      : 'Unable to establish your Coach session.';
  } catch {
    return 'Unable to establish your Coach session.';
  }
};

const restoreOrBootstrap = async (): Promise<BrowserAuthResponse | null> => {
  let response = await requestAuthState('/api/auth/session', 'GET');

  if (response.status === 401) {
    response = await requestAuthState('/api/auth/bootstrap', 'POST');
  }

  if (response.status === 401) {
    return null;
  }

  if (!response.ok) {
    throw new Error(await readErrorMessage(response));
  }

  return response.json() as Promise<BrowserAuthResponse>;
};

const requestBrowserAuthConfig = async (): Promise<BrowserAuthConfigResponse> => {
  const configResponse = await requestWithTimeout('/api/auth/browser-config', {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!configResponse.ok) {
    throw new Error(await readErrorMessage(configResponse));
  }

  return configResponse.json() as Promise<BrowserAuthConfigResponse>;
};

const initializeBrowserAuth = async (): Promise<AuthInitialization> => {
  const session = await restoreOrBootstrap();
  if (session) {
    return { session, config: null };
  }

  return {
    session: null,
    config: await requestBrowserAuthConfig(),
  };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initializationPromiseRef = useRef<Promise<AuthInitialization> | null>(null);
  const sessionRefreshPromiseRef = useRef<Promise<string | null> | null>(null);
  const authSessionRef = useRef<BrowserAuthResponse | null>(null);
  const [authSession, setAuthSession] = useState<BrowserAuthResponse | null>(null);
  const [browserConfig, setBrowserConfig] = useState<BrowserAuthConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const updateAuthSession = useCallback((nextSession: BrowserAuthResponse | null) => {
    authSessionRef.current = nextSession;
    setAuthSession(nextSession);
  }, []);

  const markUnauthenticated = (
    message?: string,
    resolvedBrowserConfig?: BrowserAuthConfigResponse | null,
  ) => {
    updateAuthSession(null);
    setError(message || null);
    setLoading(false);

    if (resolvedBrowserConfig) {
      setBrowserConfig(resolvedBrowserConfig);
    } else if (!browserConfig) {
      requestBrowserAuthConfig()
        .then(setBrowserConfig)
        .catch((configError) => {
          moduleLogger.error('Browser authentication config request failed', configError);
        });
    }
  };

  const refreshBrowserSession = useCallback(async (): Promise<string | null> => {
    if (!sessionRefreshPromiseRef.current) {
      sessionRefreshPromiseRef.current = restoreOrBootstrap()
        .then((restoredSession) => {
          if (!restoredSession) {
            return null;
          }

          updateAuthSession(restoredSession);
          setError(null);
          return restoredSession.csrfToken;
        })
        .finally(() => {
          sessionRefreshPromiseRef.current = null;
        });
    }

    return sessionRefreshPromiseRef.current;
  }, [updateAuthSession]);

  useEffect(() => configureBrowserAuthTransport({
    getCsrfToken: () => authSessionRef.current?.csrfToken || null,
    refreshSession: refreshBrowserSession,
    onUnauthorized: () => markUnauthenticated('Your Coach session has expired.'),
  }), [authSession?.csrfToken, refreshBrowserSession]);

  useEffect(() => {
    const diagnostic = consumePortalRedirectDiagnostic();
    if (diagnostic) {
      moduleLogger.info('Returned from a Portal redirect', diagnostic);
    }
  }, []);

  useEffect(() => {
    let active = true;
    initializationPromiseRef.current ??= initializeBrowserAuth();

    initializationPromiseRef.current
      .then(({ session: restoredSession, config }) => {
        if (!active) {
          return;
        }

        if (restoredSession) {
          LEGACY_AUTH_STORAGE_KEYS.forEach((storageKey) => localStorage.removeItem(storageKey));
        }

        updateAuthSession(restoredSession);
        setBrowserConfig(config);
        setError(null);
      })
      .catch((authError) => {
        if (!active) {
          return;
        }

        moduleLogger.error('Browser authentication initialization failed', authError);
        updateAuthSession(null);
        setError(authError instanceof Error ? authError.message : 'Unable to authenticate.');
        requestBrowserAuthConfig()
          .then(setBrowserConfig)
          .catch((configError) => {
            moduleLogger.error('Browser authentication config request failed', configError);
          });
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const retry = useCallback(async () => {
    setLoading(true);
    setError(null);
    setBrowserConfig(null);

    const initializationPromise = initializeBrowserAuth();
    initializationPromiseRef.current = initializationPromise;

    try {
      const { session: restoredSession, config } = await initializationPromise;
      if (restoredSession) {
        LEGACY_AUTH_STORAGE_KEYS.forEach((storageKey) => localStorage.removeItem(storageKey));
      }

      updateAuthSession(restoredSession);
      setBrowserConfig(config);
    } catch (authError) {
      moduleLogger.error('Browser authentication retry failed', authError);
      updateAuthSession(null);
      setError(authError instanceof Error ? authError.message : 'Unable to authenticate.');
      requestBrowserAuthConfig()
        .then(setBrowserConfig)
        .catch((configError) => {
          moduleLogger.error('Browser authentication config request failed', configError);
        });
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = async () => {
    const csrfToken = authSession?.csrfToken;
    let resolvedBrowserConfig = browserConfig;

    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
      });

      if (!resolvedBrowserConfig) {
        resolvedBrowserConfig = await requestBrowserAuthConfig();
      }
    } finally {
      if (resolvedBrowserConfig?.portalLoginUrl) {
        const logoutUrl = buildPortalLogoutUrl(resolvedBrowserConfig.portalLoginUrl);
        moduleLogger.info('Redirecting to Portal via explicit logout (no redirect_to)', { logoutUrl });
        recordPortalRedirectDiagnostic('explicit-logout', logoutUrl);
        window.location.assign(logoutUrl);
      } else {
        markUnauthenticated();
      }
    }
  };

  return (
    <AuthContext.Provider value={{
      authenticated: Boolean(authSession?.authenticated),
      loading,
      user: authSession?.user || null,
      csrfToken: authSession?.csrfToken || null,
      expiresAt: authSession?.expiresAt || null,
      portalLoginUrl: browserConfig?.portalLoginUrl || null,
      error,
      logout,
      retry,
      markUnauthenticated,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useBrowserAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useBrowserAuth must be used within an AuthProvider');
  }
  return context;
};
